import * as React from 'react';

import { cn } from '@heidi/utils';
import { Separator } from '../separator';

import { MobileSidebar } from './components/mobile-sidebar';
import {
  Panel,
  PanelPinned,
  PanelProvider,
  PanelTrigger,
  usePanelContext,
} from './components/panel';
import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from './components/sidebar';
import {
  pageShellContainerVariants,
  pageShellContentClasses,
  pageShellHeaderClasses,
  pageShellHeaderTriggerContainerClasses,
} from './page-shell.styles';
import type {
  PageShellContainerProps,
  PageShellContentProps,
  PageShellHeaderProps,
  PageShellMainProps,
  PageShellProps,
  PageShellSidebarProps,
} from './page-shell.types';

// ============================================================================
// PageShell Root
// ============================================================================

/**
 * PageShell wraps the SidebarProvider and PanelProvider, providing the root layout container.
 * Children should be PageShell.Sidebar and PageShell.Main.
 *
 * @example
 * ```tsx
 * <PageShell pageId={router.pathname}>
 *   <PageShell.Sidebar>...</PageShell.Sidebar>
 *   <PageShell.Main>...</PageShell.Main>
 * </PageShell>
 * ```
 */
const PageShellRoot = React.forwardRef<HTMLDivElement, PageShellProps>(
  (
    {
      'data-testid': testId,
      className,
      children,
      defaultOpen,
      defaultCollapsible,
      open,
      onOpenChange,
      pageId = 'default',
      style,
      ...props
    },
    ref
  ) => {
    return (
      <SidebarProvider
        ref={ref}
        data-testid={testId}
        defaultOpen={defaultOpen}
        defaultCollapsible={defaultCollapsible}
        open={open}
        onOpenChange={onOpenChange}
        className={className}
        style={style}
        {...props}
      >
        <PanelProvider pageId={pageId}>{children}</PanelProvider>
      </SidebarProvider>
    );
  }
);
PageShellRoot.displayName = 'PageShell';

// ============================================================================
// PageShell.Sidebar
// ============================================================================

const PageShellSidebar = React.forwardRef<HTMLDivElement, PageShellSidebarProps>(
  ({ 'data-testid': testId, children, ...props }, ref) => {
    return (
      <>
        <Sidebar ref={ref} data-testid={testId} {...props}>
          {children}
        </Sidebar>
        <PanelPinned />
      </>
    );
  }
);
PageShellSidebar.displayName = 'PageShell.Sidebar';

// ============================================================================
// PageShell.Main
// ============================================================================

/**
 * Main area wrapper for PageShell that contains the semantic <main> element.
 * Children should be PageShell.Header and PageShell.Content.
 * Automatically renders a pinned panel alongside content when panel is pinned.
 *
 * @example
 * ```tsx
 * <PageShell.Main>
 *   <PageShell.Header>...</PageShell.Header>
 *   <PageShell.Content>...</PageShell.Content>
 * </PageShell.Main>
 * ```
 */
const PageShellMain = React.forwardRef<HTMLElement, PageShellMainProps>(
  ({ 'data-testid': testId, className, children, ...props }, ref) => {
    return (
      <SidebarInset ref={ref} data-testid={testId} className={className} {...props}>
        {children}
      </SidebarInset>
    );
  }
);
PageShellMain.displayName = 'PageShell.Main';

// ============================================================================
// PageShell.Header
// ============================================================================

/**
 * Header slot for PageShell.Main that aligns with the sidebar header.
 *
 * On mobile, renders a SidebarTrigger (hamburger menu) to open the sidebar drawer.
 * On desktop, renders a PanelTrigger for toggling the secondary panel (only when panels exist).
 *
 * @example
 * ```tsx
 * <PageShell.Header>
 *   <Breadcrumb>
 *     <BreadcrumbItem>Dashboard</BreadcrumbItem>
 *   </Breadcrumb>
 * </PageShell.Header>
 * ```
 */
const PageShellHeader = React.forwardRef<HTMLElement, PageShellHeaderProps>(
  (
    {
      'data-testid': testId,
      className,
      children,
      showTrigger = true,
      showSeparator = true,
      ...props
    },
    ref
  ) => {
    const { isMobile, hasPanelContent } = usePanelContext();

    // On mobile: always show SidebarTrigger for opening the drawer
    // On desktop: show PanelTrigger only when panel content exists
    const showTriggerButton = showTrigger && (isMobile || hasPanelContent);

    return (
      <header
        ref={ref}
        data-testid={testId}
        className={cn(pageShellHeaderClasses, className)}
        {...props}
      >
        {showTriggerButton && (
          <div className="flex items-center gap-2">
            <div className={pageShellHeaderTriggerContainerClasses}>
              {isMobile ? <SidebarTrigger /> : <PanelTrigger />}
            </div>
            {showSeparator && <Separator orientation="vertical" className="me-2 h-4" />}
          </div>
        )}
        <div className="flex flex-1 items-center">{children}</div>
      </header>
    );
  }
);
PageShellHeader.displayName = 'PageShell.Header';

// ============================================================================
// PageShell.Content
// ============================================================================

/**
 * Content area slot for PageShell.Main.
 * Provides consistent padding and flex layout for primary page content.
 *
 * @example
 * ```tsx
 * <PageShell.Content>
 *   <h1>Page Title</h1>
 *   <p>Page content goes here</p>
 * </PageShell.Content>
 * ```
 */
const PageShellContent = React.forwardRef<HTMLDivElement, PageShellContentProps>(
  ({ 'data-testid': testId, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-testid={testId}
        className={cn(pageShellContentClasses, className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
PageShellContent.displayName = 'PageShell.Content';

// ============================================================================
// PageShell.Container
// ============================================================================

/**
 * Centres a page's content at one of the standard max widths, with horizontal
 * gutters outside it. Rendered by the page inside PageShell.Content; reads no
 * context, so it works under any layout.
 *
 * @example
 * ```tsx
 * <PageShell.Container width="narrow">
 *   <PageHeader heading="Settings" />
 * </PageShell.Container>
 * ```
 */
const PageShellContainer = React.forwardRef<HTMLDivElement, PageShellContainerProps>(
  ({ 'data-testid': testId, className, width = 'default', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-testid={testId}
        data-slot="page-shell-container"
        data-width={width}
        className={cn(pageShellContainerVariants({ width }), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
PageShellContainer.displayName = 'PageShell.Container';

// ============================================================================
// Compound Component
// ============================================================================

const PageShell = Object.assign(PageShellRoot, {
  Sidebar: PageShellSidebar,
  Main: PageShellMain,
  Header: PageShellHeader,
  Content: PageShellContent,
  Container: PageShellContainer,
  Panel,
  MobileSidebar,
});

export {
  PageShell,
  PageShellContainer,
  PageShellContent,
  PageShellHeader,
  PageShellMain,
  Panel as PageShellPanel,
  PageShellSidebar,
};
